/**
 * RGPDUnifiedRegistryPanel — Registre unifié RGPD (Admin / CNIL-ready).
 *
 * Affiche TOUS les types de transferts E2E (ZIP shares, emails PDF, exports CSV,
 * liens publics, exports API) dans une seule vue Article 30.
 *
 * Endpoints :
 *  - GET  /api/rgpd/registry?transfer_type=&status=&actor_email=&subject_email=
 *  - POST /api/rgpd/registry/{id}/revoke
 *  - POST /api/rgpd/registry/backfill (one-shot)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, FileArchive, Mail, FileSpreadsheet, Globe, Code,
  Search, Eye, Calendar, CheckCircle2, XCircle, Loader2, RefreshCw,
  Database, ChevronRight, Ban, AlertTriangle, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getToken } from '../services/tokenService';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TRANSFER_TYPE_META = {
  zip_share: { label: 'Partage ZIP', icon: FileArchive, color: 'bg-indigo-700 text-white border-0' },
  email_pdf: { label: 'Email PDF', icon: Mail, color: 'bg-blue-700 text-white border-0' },
  csv_export: { label: 'Export CSV', icon: FileSpreadsheet, color: 'bg-emerald-700 text-white border-0' },
  public_link: { label: 'Lien public', icon: Globe, color: 'bg-amber-700 text-white border-0' },
  api_export: { label: 'Export API', icon: Code, color: 'bg-purple-700 text-white border-0' },
};

const STATUS_META = {
  active: { label: 'Actif', color: 'bg-emerald-700 text-white border-0', icon: CheckCircle2 },
  revoked: { label: 'Révoqué', color: 'bg-amber-700 text-white border-0', icon: XCircle },
  anonymized: { label: 'Anonymisé (Art. 17)', color: 'bg-slate-800 text-white border-0', icon: Shield },
  expired: { label: 'Expiré', color: 'bg-rose-700 text-white border-0', icon: AlertTriangle },
};

function formatDateFR(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function RGPDUnifiedRegistryPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total_all: 0, by_status: {}, by_type: {} });
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [transferType, setTransferType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [subjectEmail, setSubjectEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [selected, setSelected] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const load = useCallback(async (skipOverride = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('skip', String(skipOverride ?? skip));
      if (transferType) params.set('transfer_type', transferType);
      if (statusFilter) params.set('status', statusFilter);
      if (actorEmail) params.set('actor_email', actorEmail);
      if (subjectEmail) params.set('subject_email', subjectEmail);
      const res = await axios.get(`${API}/rgpd/registry?${params}`, { headers: headers() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || { total_all: 0, by_status: {}, by_type: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger le registre unifié');
    } finally {
      setLoading(false);
    }
  }, [headers, limit, skip, transferType, statusFilter, actorEmail, subjectEmail]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const runBackfill = async () => {
    if (!window.confirm('Lancer le backfill des partages ZIP existants vers le registre unifié ?\n\n(Idempotent — peut être relancé sans risque.)')) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/rgpd/registry/backfill`, null, { headers: headers() });
      toast.success(`Backfill terminé : ${res.data.backfilled} nouveaux, ${res.data.already_exists || 0} déjà présents`);
      await load();
    } catch {
      toast.error('Erreur backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const revokeTransfer = async (transferId) => {
    if (!window.confirm('Confirmer la révocation manuelle de ce transfert ?\n\nL\'audit-trail sera conservé pour conformité Article 30.')) return;
    try {
      await axios.post(`${API}/rgpd/registry/${encodeURIComponent(transferId)}/revoke`, null, { headers: headers() });
      toast.success('Transfert révoqué');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur révocation');
    }
  };

  return (
    <div className="space-y-6" data-testid="rgpd-unified-registry-panel">
      {/* Header navy O'SCOP */}
      <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-amber-500/20 backdrop-blur flex items-center justify-center shrink-0 border border-amber-400/30">
              <Layers className="h-7 w-7 text-amber-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-0.5">REGISTRE UNIFIÉ — ARTICLE 30 RGPD</div>
              <h2 className="text-2xl font-bold tracking-tight">Tous les transferts E2E</h2>
              <p className="text-sm text-indigo-200 mt-1 max-w-2xl">
                Audit unique pour la CNIL — couvre les partages ZIP, emails PDF, exports CSV, liens publics & exports API.
                Conforme Articles 15, 17, 20, 30 & 32.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runBackfill}
              disabled={backfilling}
              className="bg-amber-500/10 text-amber-200 border-amber-400/40 hover:bg-amber-500/20"
              data-testid="rgpd-backfill-btn"
            >
              {backfilling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1" />}
              Backfill ZIP existants
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load()}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              data-testid="rgpd-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rafraîchir
            </Button>
          </div>
        </div>

        {/* KPIs par type */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-6">
          {Object.entries(TRANSFER_TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const count = stats.by_type?.[key] || 0;
            return (
              <button
                key={key}
                onClick={() => { setTransferType(transferType === key ? '' : key); setSkip(0); }}
                className={`text-left bg-white/5 backdrop-blur rounded-lg p-3 border transition-all ${
                  transferType === key
                    ? 'border-amber-400 bg-amber-500/10 ring-1 ring-amber-400/50'
                    : 'border-white/10 hover:bg-white/10'
                }`}
                data-testid={`rgpd-kpi-type-${key}`}
              >
                <Icon className="h-4 w-4 text-indigo-300 mb-1" />
                <div className="text-[10px] uppercase tracking-wider text-indigo-200">{meta.label}</div>
                <div className="text-xl font-bold">{count}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Statut</Label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
              className="w-full h-9 text-sm border border-slate-300 rounded px-2"
              data-testid="rgpd-filter-status"
            >
              <option value="">Tous</option>
              <option value="active">Actifs</option>
              <option value="revoked">Révoqués</option>
              <option value="anonymized">Anonymisés</option>
              <option value="expired">Expirés</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Email initiateur</Label>
            <Input
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="actor@..."
              className="h-9"
              data-testid="rgpd-filter-actor"
            />
          </div>
          <div>
            <Label className="text-xs">Email subject</Label>
            <Input
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              placeholder="subject@..."
              className="h-9"
              data-testid="rgpd-filter-subject"
            />
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button size="sm" onClick={() => { setSkip(0); load(0); }} className="h-9" data-testid="rgpd-search-btn">
              <Search className="h-3.5 w-3.5 mr-1" /> Rechercher
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              setTransferType(''); setStatusFilter(''); setActorEmail(''); setSubjectEmail(''); setSkip(0); load(0);
            }} className="h-9">
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-700">
            {total} transfert{total > 1 ? 's' : ''} dans le registre
          </div>
          <div className="flex gap-2 text-xs">
            {Object.entries(stats.by_status || {}).map(([s, n]) => {
              const meta = STATUS_META[s];
              if (!meta) return null;
              return (
                <Badge key={s} className={`${meta.color} font-bold shadow-sm`}>
                  {meta.label}: {n}
                </Badge>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 inline" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Aucun transfert. Lancez le backfill pour importer les partages ZIP existants.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((it) => {
              const tMeta = TRANSFER_TYPE_META[it.transfer_type] || { label: it.transfer_type, icon: FileArchive, color: 'bg-slate-800 text-white border-0' };
              const sMeta = STATUS_META[it.status] || STATUS_META.active;
              const TIcon = tMeta.icon;
              const SIcon = sMeta.icon;
              return (
                <div key={it.id} className="p-4 hover:bg-slate-50 flex items-start gap-4" data-testid={`rgpd-item-${it.id}`}>
                  <Badge className={`${tMeta.color} flex items-center gap-1 shrink-0 font-bold shadow-sm`}>
                    <TIcon className="h-3 w-3" />
                    {tMeta.label}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {it.payload_summary?.zip_filename || it.payload_summary?.filename || it.purpose || it.id.slice(0, 8)}
                      </span>
                      <Badge className={`${sMeta.color} text-[10px] font-bold shadow-sm`}>
                        <SIcon className="h-2.5 w-2.5 mr-0.5" />
                        {sMeta.label}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
                      <span>Subject: <span className="font-medium">{it.subject_email || it.subject_user_id?.slice(0, 8) || '—'}</span></span>
                      {it.recipient_email && <span>→ {it.recipient_email}</span>}
                      <span><Calendar className="inline h-3 w-3 mr-0.5" />{formatDateFR(it.created_at)}</span>
                      {it.payload_summary?.zip_size_bytes && <span>📦 {formatBytes(it.payload_summary.zip_size_bytes)}</span>}
                      {(it.access_count !== undefined && it.access_count !== null) && <span>👁 {it.access_count} accès</span>}
                      <span className="text-slate-400">Base légale: {it.legal_basis}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {it.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => revokeTransfer(it.id)}
                        className="text-amber-600 hover:bg-amber-50"
                        data-testid={`rgpd-revoke-${it.id}`}
                        title="Révoquer ce transfert"
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelected(it)}
                      data-testid={`rgpd-view-${it.id}`}
                      title="Détails"
                    >
                      <Eye className="h-4 w-4" />
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > limit && (
          <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
            <span>{skip + 1}–{Math.min(skip + limit, total)} sur {total}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => { const ns = Math.max(0, skip - limit); setSkip(ns); load(ns); }}>Précédent</Button>
              <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => { const ns = skip + limit; setSkip(ns); load(ns); }}>Suivant</Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal détail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Détail du transfert</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-xs max-h-[70vh] overflow-auto">
              <div className="bg-slate-50 rounded p-3 grid grid-cols-2 gap-2">
                <div><span className="text-slate-400">ID :</span> <code className="text-[10px]">{selected.id}</code></div>
                <div><span className="text-slate-400">Type :</span> {TRANSFER_TYPE_META[selected.transfer_type]?.label || selected.transfer_type}</div>
                <div><span className="text-slate-400">Statut :</span> {STATUS_META[selected.status]?.label || selected.status}</div>
                <div><span className="text-slate-400">Base légale :</span> {selected.legal_basis}</div>
                <div className="col-span-2"><span className="text-slate-400">Finalité :</span> {selected.purpose || '—'}</div>
                <div><span className="text-slate-400">Créé :</span> {formatDateFR(selected.created_at)}</div>
                <div><span className="text-slate-400">Rétention jusqu'au :</span> {formatDateFR(selected.retention_until)}</div>
                {selected.revoked_at && <div><span className="text-slate-400">Révoqué :</span> {formatDateFR(selected.revoked_at)}</div>}
                {selected.anonymized_at && <div><span className="text-slate-400">Anonymisé :</span> {formatDateFR(selected.anonymized_at)}</div>}
              </div>

              <div>
                <div className="font-semibold mb-1">Payload</div>
                <pre className="bg-slate-50 rounded p-2 overflow-auto text-[10px]">{JSON.stringify(selected.payload_summary || {}, null, 2)}</pre>
              </div>

              {Array.isArray(selected.access_logs) && selected.access_logs.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Accès ({selected.access_count})</div>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {selected.access_logs.map((log, i) => (
                      <div key={i} className="bg-slate-50 rounded px-2 py-1 text-[11px]">
                        <span className="font-medium">{log.accessed_by_email || '—'}</span>
                        <span className="text-slate-400"> · {formatDateFR(log.accessed_at)} · IP {log.ip || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
